import React, { FC, ReactText, useMemo } from 'react';
import { Alert, Dropdown, MenuProps } from 'antd';
import type { Store } from 'antd/lib/form/interface';
import type { PaginationConfig } from 'antd/lib/pagination';
import type {
  SorterResult,
  TableCurrentDataSource,
} from 'antd/lib/table/interface';
import { mainDateFormat } from '@core/utils/methods';
import {
  BPExpirationReportResponse,
  OptionProps,
  PermittingOverviewReportResponse,
  ReportKey,
} from '@models/interfaces';
import { CustomTable } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import {
  getPermittingColumns,
  PermittingRow,
} from '../../models/report-columns/permitting-columns';
import {
  isInteractiveRowClick,
  openReportRowInNewTab,
} from '../../models/report-navigation';
import { useReportExport } from '../../models/use-report-export';

import './styles.scss';

interface PermittingReportProps {
  reportKey: ReportKey.PermittingOverview | ReportKey.BPExpiration;
  data?: BPExpirationReportResponse | PermittingOverviewReportResponse;
  loading: boolean;
  dateFields: OptionProps[];
  sortByOptions: OptionProps[];
  onTableChange: (
    pagination: PaginationConfig,
    tableFilters: Record<string, ReactText[] | null>,
    sorter: SorterResult<Store> | SorterResult<Store>[],
    extra: TableCurrentDataSource<Store>,
  ) => void;
}

export const PermittingReport: FC<PermittingReportProps> = ({
  reportKey,
  data,
  loading,
  dateFields,
  sortByOptions,
  onTableChange,
}) => {
  const overview = reportKey === ReportKey.PermittingOverview;
  const columns = useMemo(
    () =>
      getPermittingColumns(
        reportKey,
        data?.filtersApplied ?? {},
        sortByOptions,
        dateFields,
      ),
    [reportKey, data, sortByOptions, dateFields],
  );
  const rows: PermittingRow[] = data?.table.items ?? [];
  const title = overview
    ? 'Permitting Overview Report'
    : 'BP Expiration Report';
  const { exporting, exportError, onExportExcel, onExportPdf } =
    useReportExport({
      columns,
      metrics: [],
      rows,
      reportTitle: title,
      chartsMode: 'none',
      scope: {
        asOfDate: data?.asOfDate,
        timeZone: data?.timeZone,
        filters: data?.filtersApplied ?? {},
        page: data?.table.page ?? 1,
        size: data?.table.size ?? 25,
        total: data?.table.total ?? 0,
      },
    });
  const exportMenu: MenuProps = {
    items: [
      { key: 'excel', label: 'Excel — current page', onClick: onExportExcel },
      { key: 'pdf', label: 'PDF — current page', onClick: onExportPdf },
    ],
  };

  return (
    <div className="prov-permitting-report">
      <div className="prov-permitting-report__toolbar">
        <div className="prov-permitting-report__description">
          <p>
            {overview
              ? 'Actual dates from Permitting Workflow, one row per project.'
              : 'Includes overdue permits and permits expiring within the next 60 calendar days. Colors use business days, excluding weekends and observed annual US federal holidays.'}
          </p>
          {data && (
            <p>
              Calculated as of {mainDateFormat(data.asOfDate)} ({data.timeZone})
            </p>
          )}
        </div>
        <Dropdown
          menu={exportMenu}
          trigger={['click']}
          disabled={!data || loading || exporting || !rows.length}
        >
          <PrimaryButton
            title={exporting ? 'Exporting…' : 'Export current page ▾'}
          />
        </Dropdown>
      </div>
      {exportError && <Alert type="error" message={exportError} />}
      <CustomTable
        module="reports"
        subModule={reportKey}
        columns={columns}
        dataSource={rows}
        onChange={onTableChange}
        onRowClick={(
          row: PermittingRow,
          _newTab: boolean,
          event: { target?: EventTarget | null },
        ) => {
          if (!isInteractiveRowClick(event))
            openReportRowInNewTab(
              `/permitting/${row.projectBundleID}/details?tabName=Workflow`,
            );
        }}
        pagination={{
          current: data?.table.page,
          pageSize: data?.table.size,
          total: data?.table.total,
        }}
        rowKey="projectId"
        rowClassName="prov-permitting-report__row"
        scroll={{ x: overview ? 3200 : 1220, y: 'auto' }}
      />
    </div>
  );
};
